import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { BusinessProfile } from "../../../types/business";
import { RootState } from "../../../store";
import * as triplinkService from '../../../utils/triplinkService'
import { logout } from "../auth/authSlice";
import { fetchExpert } from "../expert/expertSlice";

interface BusinessState {
  body: BusinessProfile | null;
  updating: boolean;
}
interface UpdateBusinessParams {
  data: Record<string, any>
}

interface UpdateDomainParam {
  domain: string
}

const initialState: BusinessState = {
  body: null,
  updating: false
}

// Thunks
export const fetchBusiness = createAsyncThunk(
  'business/fetchBusiness',
  async (params, { dispatch, getState }) => {
    const state: RootState = (getState() as RootState);
    const accessToken = state.accessToken.body;
    if (!accessToken) {
      throw new Error('Cannot fetchBusiness without accessToken')
    }
    const result = await triplinkService.getMyBusinessProfile(accessToken)

    return {
      business: result,
      requiresAccessToken: true,
    }
  }
)

export const updateBusiness = createAsyncThunk(
  'business/updateBusiness',
  async (params: UpdateBusinessParams, { dispatch, getState }) => {
    const state: RootState = (getState() as RootState);
    const accessToken = state.accessToken.body;
    if (!accessToken) {
      throw new Error('Cannot updateBusiness without accessToken')
    }
    const result = await triplinkService.updateMyBusinessProfile(accessToken, params.data)

    return {
      business: result,
      requiresAccessToken: true,
    }
  }
)

export const updateBusinessDomain = createAsyncThunk(
  'business/updateBusinessDomain',
  async (params: UpdateDomainParam, {dispatch, getState}) => {
    const state: RootState = (getState() as RootState);
    const accessToken = state.accessToken.body;
    if (!accessToken) {
      throw new Error('Cannot updateBusiness without accessToken')
    }
    const result = await triplinkService.setDomainName(accessToken, params)
    dispatch(fetchExpert({requiresAccessToken: true}))

    return {
      domain: result.domain,
      requiresAccessToken: true,
    }
  }
)

const businessSlice = createSlice({
  name: 'business',
  initialState,
  reducers: {
  },
  extraReducers(builder) {
    builder
      .addCase(fetchBusiness.pending, (state) => {
        state.updating = true
      })
      .addCase(fetchBusiness.fulfilled, (state, action) => {
        state.body = action.payload.business
        state.updating = false
      })
      .addCase(fetchBusiness.rejected, (state) => {
        state.updating = false
      })
      .addCase(updateBusiness.pending, (state) => {
        state.updating = true
      })
      .addCase(updateBusiness.fulfilled, (state, action) => {
        state.body = action.payload.business
        state.updating = false
      })
      .addCase(updateBusiness.rejected, (state) => {
        state.updating = false
      })
      .addCase(updateBusinessDomain.pending, (state) => {
        state.updating = true
      })
      .addCase(updateBusinessDomain.fulfilled, (state, action) => {
        if (state.body) {
          state.updating = false
          state.body = {
            ...state.body,
            domain: action.payload.domain
          }
        }
      })
      .addCase(updateBusinessDomain.rejected, (state) => {
        state.updating = false
      })
      .addCase(logout, (state) => {
        state.body = null
        state.updating = false
      })
  }
})

export const {
} = businessSlice.actions

export const selectBusiness = (state: RootState) => state.business

export default businessSlice.reducer